import { isUndefined } from "lodash";
import { isString, isNumber } from "angular";
import * as moment from "moment-timezone";
import { ISettingsService } from "../typings/typings";

export function addSlashWhenSet() {
    return function(path) {
        if (typeof path !== "undefined" && path !== "" && path !== "undefined" && path !== null) {
            return path + "/";
        } else {
            return "";
        }
    };
}

/**
 * Format date-time
 */

export const timeUntil = [
    "SettingsService",
    SettingsService => {
        return function(dateTime: string): number {
            if (!dateTime) {
                return;
            }

            const timezone = SettingsService.user.timezone.name;
            const dateMoment = moment.tz(dateTime, timezone);

            return moment(dateMoment.endOf("day")).diff(moment(), "days");
        };
    },
];

export const formatDateTime = [
    "$filter",
    "SettingsService",
    ($filter, SettingsService) => {
        const shortFormat = "DD MMM YYYY";

        function tr(str) {
            let $translate = $filter("translate");
            return $translate(str);
        }

        function getDateFormat(diffDays: number) {
            const prefix =
                diffDays >= 0 && diffDays < 1
                    ? "[" + tr("common:date-today") + "]"
                    : diffDays >= 1 && diffDays < 2
                        ? "[" + tr("common:date-tomorrow") + "]"
                        : "dddd";

            return prefix + ", DD MMM YYYY HH:mm z";
        }

        function getFullFormat(dateMoment: moment.Moment, timezone: string): string {
            const startOfDay = moment.tz(timezone).startOf("day");
            const diff = dateMoment.diff(startOfDay, "days", true);

            return getDateFormat(diff);
        }

        return function(dateTime: string, short: boolean): string {
            if (!dateTime) {
                return;
            }

            const timezone = SettingsService.user.timezone.name;
            const dateMoment = moment.tz(dateTime, timezone);

            return dateMoment.format(short ? shortFormat : getFullFormat(dateMoment, timezone));
        };
    },
];

export const formatDate = [
    "SettingsService",
    SettingsService => {
        return function(dateTime) {
            if (!dateTime) {
                return;
            }

            const timezone = SettingsService.user.timezone.name;
            const dateMoment = moment.tz(dateTime, timezone);
            const format = "DD MMM YYYY";

            return dateMoment.format(format);
        };
    },
];

/**
 * Returns a string for a given amount of seconds, optionally limited by the maxdepth parameter with a maxdepth
 * of 2 meaning a maximum of two time units are displayed, starting with the biggest first.
 * Default is three, displaying the whole days, hours and minutes.
 */

export const secondsAsText = [
    "$filter",
    $filter => {
        return function(durationInSeconds, maxdepth) {
            maxdepth = maxdepth || 3;
            let depth = 1;
            let d = moment.duration(durationInSeconds, "seconds");
            let days = d.days();
            let hours = d.hours();
            let minutes = d.minutes();
            let seconds = d.seconds();

            let $translate = $filter("translate");

            let text = "";
            if (days >= 1) {
                text += $translate("common:n-days", { days: days });
                depth++;
            }

            if (hours >= 1 && depth <= maxdepth) {
                if (text.length > 0) {
                    text += " ";
                }
                text += $translate("common:n-hours", { hours: hours });
                depth++;
            }

            if (minutes >= 1 && depth <= maxdepth) {
                if (text.length > 0) {
                    text += " ";
                }
                text += $translate("common:n-minutes", { minutes: minutes });
            }

            if (text.length === 0) {
                text = $translate("common:n-seconds", { seconds: seconds });
            }

            return text;
        };
    },
];

/**
 * Returns the seconds in future as date for now, or if currentDateTime is set, for the given Date
 */

export const secondsAsDate = [
    "SettingsService",
    SettingsService => {
        return function(seconds, currentDateTime) {
            if (isUndefined(currentDateTime)) {
                currentDateTime = new Date().getTime();
            }

            let destDateMoment = moment.tz(seconds * 1000 + currentDateTime, SettingsService.user.timezone.name);
            return destDateMoment.format("dddd, DD MMM HH:mm z");
        };
    },
];

/**
 * Checks if a promise object is empty, e.g. it does not contain any data except the normal $promise and
 * $resolved members. Can also be used to check if a normal javascript object does not have any members.
 */

export function isEmptyPromise() {
    return function(object) {
        for (let key in object) {
            if (object.hasOwnProperty(key) && ["$promise", "$resolved"].indexOf(key) === -1) {
                return false;
            }
        }
        return true;
    };
}

export function sprintf() {
    function parse(args) {
        let parts = [].slice.call(args, 1),
            i = 0;

        return args[0].replace(/%s/g, function() {
            return parts[i++];
        });
    }

    return function() {
        return parse(arguments);
    };
}

export function signedNumber() {
    return function(input) {
        if (!isNumber(input)) {
            return input;
        } else {
            return input > 0 ? "+" + input : input;
        }
    };
}

export function ucfirst() {
    return function(str) {
        if (isString(str)) {
            let f = str.charAt(0).toUpperCase();
            return f + str.substr(1);
        }
        return str;
    };
}

export const addUtmParams = [
    "SettingsService",
    (SettingsService: ISettingsService) => {
        let stripLegacyPrefix = function(str) {
            let pattern = new RegExp("^(/)?(" + SettingsService.legacy_content.prefix + ")(.*)");
            return str.replace(pattern, "$1$3");
        };

        let nonEmpty = function(str) {
            return str.length > 0;
        };

        return function(str, utmSource, utmMedium, utmTerm, utmContent, utmCampaign) {
            let pathParts = stripLegacyPrefix(utmSource)
                .split("/")
                .filter(nonEmpty)
                .slice(0, 2);
            let filteredUtmSource = pathParts.join("_");

            return (
                str +
                "?utm_source=" +
                filteredUtmSource +
                "&utm_medium=" +
                utmMedium +
                "&utm_term=" +
                utmTerm +
                "&utm_content=" +
                utmContent +
                "&utm_campaign=" +
                utmCampaign
            );
        };
    },
];

export function ceil() {
    return function(number) {
        if (isNumber(number)) {
            return Math.ceil(number);
        }
        return number;
    };
}

export function abs() {
    return function(number) {
        if (isNumber(number)) {
            return Math.abs(number);
        }
        return number;
    };
}

export function filterSwissResult() {
    return function(input, search) {
        const searchRegex = new RegExp(search, "gi");

        return search
            ? input.filter(match => {
                  const contestant1 = match
                      .getMatchRankingEntries()[0]
                      .getContestant()
                      .getName();
                  const contestant2 = match
                      .getMatchRankingEntries()[1]
                      .getContestant()
                      .getName();

                  return (contestant1 && contestant1.match(searchRegex)) || (contestant2 && contestant2.match(searchRegex));
              })
            : input;
    };
}
